import { AreaSeries, BarSeries, GlyphSeries, LineSeries } from '@visx/xychart';
import { trim, uniqBy } from 'lodash';

import {
    GroupConfig,
    LegendConfig,
    StudioVisualizationType,
    XAxisConfig,
} from '@blockworks/platform/api/research/studio/visualizations';

import { GetVisualizationPropsResponse } from '../../../models';

enum SeriesComponentType {
    Line = 'line',
    Area = 'area',
    Bar = 'bar',
    Scatter = 'scatter',
}

const seriesTypeOptions = [
    { label: 'Line', value: SeriesComponentType.Line },
    { label: 'Area', value: SeriesComponentType.Area },
    { label: 'Bar', value: SeriesComponentType.Bar },
    { label: 'Scatter', value: SeriesComponentType.Scatter },
];

const yAxisOptions = [
    { label: 'Left', value: 'left' },
    { label: 'Right', value: 'right' },
    { label: 'None', value: 'none' },
];

const strokeWidthOptions = [
    { label: '1', value: '1' },
    { label: '1.25', value: '1.25' },
    { label: '1.5', value: '1.5' },
    { label: '1.75', value: '1.75' },
    { label: '2', value: '2' },
];

const BRUSH_HEIGHT = 48;
const BRUSH_HANDLE_CLASSNAME = 'timeseries-brush-handle';
const BRUSH_BOX_CLASSNAME = 'timeseries-brush-box';
const BRUSH_BOX_HTML_ID = 'timeseries-brush-box';
const TICK_LABEL_CLASSNAME = 'timeseries-tick-label';
const GRID_LINE_CLASSNAME = 'visx-line';

const TimeseriesComponents = {
    [SeriesComponentType.Line]: LineSeries,
    [SeriesComponentType.Area]: AreaSeries,
    [SeriesComponentType.Bar]: BarSeries,
    [SeriesComponentType.Scatter]: GlyphSeries,
};

interface ValueScale {
    min: number;
    max: number;
    delta: number;
}
interface GroupScale extends ValueScale {
    cols: any[];
    toPrimary?: (number: number) => number;
    fromPrimary?: (number: number) => number;
}

const createConvertor = (pScale: ValueScale, sScale: ValueScale) => {
    return (col: number) => {
        const factor = pScale.delta === sScale.delta ? 1 : pScale.delta / sScale.delta;
        const adjusted = col - sScale.min;
        if (adjusted === 0 && pScale.min === 0) {
            return col;
        }
        return pScale.min + adjusted * factor;
    };
};

const marginVariants = {
    base: { left: 24, bottom: 12, right: 24, top: 24 },
    condensed: { left: 24, bottom: 12, right: 24, top: 12 },
};

const calculateMargin = ({
    groups,
    brush,
    legend = { position: 'none' },
    xAxis,
    isSmallArea = false,
    canShowLegend,
    tickLength,
}: {
    isSmallArea?: boolean;
    brush?: boolean;
    groups: GroupConfig<any>[];
    legend: LegendConfig | undefined;
    xAxis: XAxisConfig<any>;
    canShowLegend?: boolean;
    tickLength: number;
    tickWidth: number;
}) => {
    const defaultMargin = { ...marginVariants[isSmallArea ? 'condensed' : 'base'] };
    const yAxii = uniqBy(
        groups.filter(({ axis }) => axis.orientation !== 'none'),
        'axis.orientation',
    );

    if (xAxis) {
        defaultMargin.bottom += 28;
    }
    if (trim(xAxis.title) && trim(xAxis.title).length > 0) {
        defaultMargin.bottom += 14;
    }

    if (xAxis.tickStrategy === 'all') {
        defaultMargin.bottom += tickLength;
    }

    yAxii.forEach(({ axis }) => {
        if (axis.orientation !== 'none') {
            const baseOffset = 28;
            const titleOffset = axis.title && axis.title.length > 0 ? 14 : 0;

            defaultMargin[axis.orientation] += baseOffset + titleOffset;
        }
    });

    const legendPos = legend?.position;
    if (legendPos !== 'none' && canShowLegend) {
        if (legendPos && ['top', 'bottom'].includes(legendPos)) {
            defaultMargin[legendPos] += 20;
        } else if (legendPos && ['left', 'right'].includes(legendPos)) {
            defaultMargin[legendPos] += 20;
            defaultMargin[legendPos] += 140;
        }
    }

    if (brush) {
        defaultMargin.bottom += BRUSH_HEIGHT;
    }

    return defaultMargin;
};

const seriesAxisConfig = {
    base: { xAxis: { paddingInner: 0.1, align: 0 } },
    bar: { xAxis: { paddingInner: 0.1, align: 0 } },
    area: { xAxis: { paddingInner: 0.5, align: 0 } },
    line: { xAxis: { paddingInner: 0.5, align: 0 } },
    scatter: { xAxis: { paddingInner: 0.5, align: 0 } },
};

type TimeseriesProps = GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>;

export type { GroupScale, TimeseriesProps, ValueScale };
export {
    BRUSH_BOX_CLASSNAME,
    BRUSH_BOX_HTML_ID,
    BRUSH_HANDLE_CLASSNAME,
    BRUSH_HEIGHT,
    calculateMargin,
    createConvertor,
    GRID_LINE_CLASSNAME,
    seriesAxisConfig,
    seriesTypeOptions,
    strokeWidthOptions,
    TICK_LABEL_CLASSNAME,
    TimeseriesComponents,
    yAxisOptions,
};
